import { ArrowLeft, Download, Pencil } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { BannerPreview } from './banner-preview'
import { getAnnouncementOrganizationColumns } from './columns'
import { AnnouncementStatusBadge } from './status-badge'
import {
  ANNOUNCEMENT_TYPE_LABELS,
  buildCsvFilename,
  downloadBlob,
  formatDateTime,
  formatGermanDate,
  getErrorMessage,
  ORGANIZATION_STATUS_LABELS
} from './utils'

import {
  ANNOUNCEMENT_ORGANIZATION_STATUSES,
  AnnouncementOrganizationStatusValue,
  AnnouncementSummary,
  exportAnnouncementOrganizationsCsv
} from '@/api/announcements-client'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card'
import { DataTable } from '@/components/ui/data-table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { toast } from '@/components/ui/use-toast'
import { useAuth } from '@/providers/AuthContextProvider'
import { RoutePaths } from '@/routes/routes'
import {
  useMutationArchiveAnnouncement,
  useMutationPublishAnnouncement,
  useQueryGetAnnouncement,
  useQueryGetAnnouncementOrganizations
} from '@/services/announcements.service'
import { useQueryGetOrganizations } from '@/services/organization.service'
import { isPermitted, Permissions } from '@/utils/permission-manager'

const ALL_STATUSES = 'all'

const SUMMARY_TILES: { key: keyof AnnouncementSummary; label: string }[] = [
  { key: 'eligible', label: 'Eligible' },
  { key: 'acknowledged', label: 'Acknowledged' },
  { key: 'pending', label: 'Pending' },
  { key: 'expired', label: 'Expired' },
  { key: 'superseded', label: 'Superseded' }
]

const DEFAULT_ORG_COLUMNS_TO_HIDE = {
  acknowledged_by_user_id: false
}

const MetaItem = ({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}) => (
  <div className="space-y-0.5">
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className="text-sm text-primary break-all">{children}</dd>
  </div>
)

type ConfirmAction = 'publish' | 'archive' | null

const AnnouncementDetail = () => {
  const { announcementId = '' } = useParams<{ announcementId: string }>()
  const navigate = useNavigate()
  const { userData } = useAuth()
  const canManage = Boolean(
    userData && isPermitted(Permissions.MANAGE_ANNOUNCEMENTS, userData)
  )

  const [statusFilter, setStatusFilter] = useState<
    AnnouncementOrganizationStatusValue | typeof ALL_STATUSES
  >(ALL_STATUSES)
  const status = statusFilter === ALL_STATUSES ? undefined : statusFilter
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null)
  const [isExporting, setIsExporting] = useState(false)

  const {
    data: announcement,
    isLoading,
    isError
  } = useQueryGetAnnouncement(announcementId)
  const isDraft = announcement?.status === 'draft'
  const { data: organizationsResponse, isLoading: isOrganizationsLoading } =
    useQueryGetAnnouncementOrganizations(announcementId, status, {
      enabled: Boolean(announcementId) && Boolean(announcement) && !isDraft
    })
  const { data: organizations } = useQueryGetOrganizations()
  const publishAnnouncement = useMutationPublishAnnouncement()
  const archiveAnnouncement = useMutationArchiveAnnouncement()

  const orgNames = useMemo(
    () =>
      new Map(
        (organizations ?? [])
          .filter((org) => org.id)
          .map((org) => [String(org.id), org.name ?? ''])
      ),
    [organizations]
  )
  const columns = useMemo(
    () => getAnnouncementOrganizationColumns(orgNames),
    [orgNames]
  )

  const summary = announcement?.summary ?? organizationsResponse?.summary

  const handleExportCsv = async () => {
    setIsExporting(true)

    try {
      const blob = await exportAnnouncementOrganizationsCsv(
        announcementId,
        status
      )

      downloadBlob(blob, buildCsvFilename(announcement?.list_version, status))
    } catch (error) {
      toast({
        title: 'Export failed',
        description: getErrorMessage(error),
        variant: 'destructive'
      })
    } finally {
      setIsExporting(false)
    }
  }

  const handleConfirm = () => {
    const mutation =
      confirmAction === 'publish' ? publishAnnouncement : archiveAnnouncement

    mutation.mutate(announcementId, {
      onSuccess: () => {
        toast({
          title:
            confirmAction === 'publish'
              ? 'Announcement published'
              : 'Announcement archived',
          description:
            confirmAction === 'publish'
              ? 'The banner is now shown to all eligible organizations.'
              : 'The banner is no longer shown.'
        })
        setConfirmAction(null)
      },
      onError: (error) => {
        toast({
          title:
            confirmAction === 'publish' ? 'Publish failed' : 'Archive failed',
          description: getErrorMessage(error),
          variant: 'destructive'
        })
      }
    })
  }

  if (isError) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-destructive">
          Could not load the announcement.
        </p>
        <Button
          onClick={() => navigate(RoutePaths.ANNOUNCEMENTS)}
          variant="outline"
        >
          Back to announcements
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <Button
            className="-ml-3 h-8"
            onClick={() => navigate(RoutePaths.ANNOUNCEMENTS)}
            variant="ghost"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Announcements
          </Button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-primary">
              {announcement
                ? `${
                    ANNOUNCEMENT_TYPE_LABELS[announcement.type] ??
                    announcement.type
                  } · ${announcement.list_version}`
                : 'Announcement'}
            </h1>
            <AnnouncementStatusBadge status={announcement?.status} />
          </div>
        </div>
        {canManage && announcement && (
          <div className="flex flex-wrap gap-2">
            {isDraft && (
              <Button
                onClick={() =>
                  navigate(`${RoutePaths.ANNOUNCEMENTS}/${announcementId}/edit`)
                }
                variant="outline"
              >
                <Pencil className="mr-2 h-4 w-4" />
                Edit draft
              </Button>
            )}
            {announcement.status !== 'archived' && (
              <Button
                onClick={() => setConfirmAction('archive')}
                variant="outline"
              >
                Archive
              </Button>
            )}
            {isDraft && (
              <Button onClick={() => setConfirmAction('publish')}>
                Publish
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-primary">Details</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-24 bg-muted rounded-lg animate-pulse" />
            ) : (
              <dl className="grid grid-cols-2 gap-4">
                <MetaItem label="Effective date">
                  {formatGermanDate(announcement?.effective_date, '–')}
                </MetaItem>
                <MetaItem label="Window">
                  {announcement?.window_days ?? '–'} days
                </MetaItem>
                <MetaItem label="Published at">
                  {formatDateTime(announcement?.published_at)}
                </MetaItem>
                <MetaItem label="Expires at">
                  {formatDateTime(announcement?.expires_at)}
                </MetaItem>
                <MetaItem label="Published by">
                  {announcement?.published_by || '–'}
                </MetaItem>
                <MetaItem label="Created">
                  {formatDateTime(announcement?.created_at)}
                  {announcement?.created_by
                    ? ` · ${announcement.created_by}`
                    : ''}
                </MetaItem>
                <MetaItem label="Link">
                  {announcement?.link_url ? (
                    <a
                      className="underline underline-offset-2"
                      href={announcement.link_url}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {announcement.link_url}
                    </a>
                  ) : (
                    '–'
                  )}
                </MetaItem>
                <MetaItem label="ID">{announcement?.id}</MetaItem>
              </dl>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-primary">Banner</CardTitle>
            <CardDescription>
              Shown to eligible organizations (created before publishing) until
              acknowledged or until the window closes.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <BannerPreview
              linkUrl={announcement?.link_url}
              messageDe={announcement?.message?.de}
              messageEn={announcement?.message?.en}
            />
          </CardContent>
        </Card>
      </div>

      {isDraft ? (
        <p className="text-sm text-muted-foreground">
          Acknowledgement tracking starts once the announcement is published.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
            {SUMMARY_TILES.map((tile) => (
              <Card key={tile.key}>
                <CardHeader className="pb-2">
                  <CardDescription>{tile.label}</CardDescription>
                  <CardTitle
                    className="text-3xl text-primary"
                    data-testid={`summary-${tile.key}`}
                  >
                    {summary ? summary[tile.key] : '–'}
                  </CardTitle>
                </CardHeader>
              </Card>
            ))}
          </div>

          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-primary">
                Organizations
              </h2>
              <div className="flex items-center gap-2">
                <Select
                  onValueChange={(value) =>
                    setStatusFilter(
                      value as
                        | AnnouncementOrganizationStatusValue
                        | typeof ALL_STATUSES
                    )
                  }
                  value={statusFilter}
                >
                  <SelectTrigger className="h-8 w-[180px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_STATUSES}>All statuses</SelectItem>
                    {ANNOUNCEMENT_ORGANIZATION_STATUSES.map((value) => (
                      <SelectItem key={value} value={value}>
                        {ORGANIZATION_STATUS_LABELS[value]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  className="h-8"
                  loading={isExporting}
                  onClick={handleExportCsv}
                  variant="outline"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Export CSV
                </Button>
              </div>
            </div>
            <DataTable
              columns={columns}
              data={organizationsResponse?.results ?? []}
              defaultColumnVisibility={DEFAULT_ORG_COLUMNS_TO_HIDE}
              defaultSorting={[{ id: 'org_name', desc: false }]}
              hideColumnFilters
              isLoading={isLoading || isOrganizationsLoading}
              tableConfigName="announcement-organizations"
              toolbarProps={{
                filterPlaceholder: 'Search org ID or name...',
                exportButtonProps: { className: 'hidden' }
              }}
            />
          </div>
        </>
      )}

      <Dialog
        onOpenChange={(open) => !open && setConfirmAction(null)}
        open={confirmAction !== null}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-primary">
              {confirmAction === 'publish'
                ? 'Publish announcement to all organizations?'
                : 'Archive announcement?'}
            </DialogTitle>
            <DialogDescription>
              {confirmAction === 'publish' ? (
                <>
                  The banner will be shown in the 360 portal of{' '}
                  <strong>all eligible organizations</strong> (created before
                  now) for {announcement?.window_days ?? '–'} days or until an
                  admin acknowledges it. Any older open announcement becomes
                  superseded.{' '}
                  <strong>
                    A published announcement cannot be edited afterwards.
                  </strong>
                </>
              ) : (
                <>
                  The banner will stop showing for all organizations
                  immediately. Existing acknowledgements stay in the log. This
                  cannot be undone.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setConfirmAction(null)} variant="outline">
              Cancel
            </Button>
            <Button
              loading={
                publishAnnouncement.isPending || archiveAnnouncement.isPending
              }
              onClick={handleConfirm}
              variant={confirmAction === 'archive' ? 'destructive' : 'default'}
            >
              {confirmAction === 'publish' ? 'Publish to all orgs' : 'Archive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default AnnouncementDetail
